import type { RampConfig, RampShape } from '@gorampe/calc-engine';
import { optimizeTreadSections } from '@gorampe/calc-engine';
import type {
  SceneLayout,
  PlatformPlacement,
  TreadPlacement,
  RailPlacement,
  LandingPlacement,
  PattePlacement,
  CornerPostPlacement,
  TransitionPlacement,
} from '../types';
import { SCALE_IN_TO_UNIT } from '../types';
import type { HardwareGroup } from '../hardware/instances';
import {
  DEFAULT_HEAD_PLATFORM_DEPTH_IN,
  DEFAULT_HEAD_PLATFORM_WIDTH_IN,
  DEFAULT_TURN_LANDING_SIZE_IN,
  EDGE_OVERLAP_IN,
} from './constants';
import { selectPatteSku, selectTransitionSku, selectCornerPostSku } from '../bom/skuMapping';

type SBShape = Extract<RampShape, { kind: 'switchback' }>;

const PATTE_INTERVAL_IN = 60; // patte every 5 ft

export function layoutSwitchback(config: RampConfig, shape: SBShape): SceneLayout {
  const platforms: PlatformPlacement[] = [];
  const treads: TreadPlacement[] = [];
  const rails: RailPlacement[] = [];
  const landings: LandingPlacement[] = [];
  const pattes: PattePlacement[] = [];
  const cornerPosts: CornerPostPlacement[] = [];
  const transitions: TransitionPlacement[] = [];

  // PDF officiel: head platform 60×60 (default) or 60×96. Turn landings 60×60.
  const headDepthIn = config.headPlatformDepthIn ?? DEFAULT_HEAD_PLATFORM_DEPTH_IN;
  const headWidthIn = DEFAULT_HEAD_PLATFORM_WIDTH_IN;
  const TURN_LANDING_SIZE_IN = DEFAULT_TURN_LANDING_SIZE_IN;

  const totalRun = shape.segments.reduce((a, s) => a + s.runIn, 0);

  // PDF officiel: 60×60 (5×5) or 60×96 (5×8), independent of arm width.
  platforms.push({
    id: 'head',
    kind: 'head',
    position: {
      x: 0,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT,
    },
    widthIn: headWidthIn,
    depthIn: headDepthIn,
  });

  const rh = config.usage === 'com' ? 42 : 36;

  // ── Walk segments accumulating cursor position / orientation / height ──
  let cursorX = 0;
  let cursorZ = 0;
  let cursorRot = 0; // Y-rotation of current travel direction
  let heightRemaining = config.heightIn;

  shape.segments.forEach((seg, idx) => {
    const segHeight = config.heightIn * (seg.runIn / totalRun);
    // POSITIF — descente vers +Z (dans le référentiel du segment)
    const pitchAngle = +Math.atan2(segHeight, seg.runIn);
    const segMidYIn = heightRemaining - segHeight / 2; // Y at mid-height of this segment

    // ── Treads for this segment ──
    let cIn = 0;
    for (const sec of optimizeTreadSections(seg.runIn)) {
      for (let i = 0; i < sec.qty; i++) {
        const midRun = cIn + sec.lengthIn / 2;
        const yIn = heightRemaining - (midRun / seg.runIn) * segHeight;
        const dx = Math.sin(cursorRot) * midRun;
        const dz = Math.cos(cursorRot) * midRun;
        treads.push({
          id: `tread-sb${idx}-${sec.lengthIn}-${i}-${cIn}`,
          position: {
            x: (cursorX + dx) * SCALE_IN_TO_UNIT,
            y: yIn * SCALE_IN_TO_UNIT,
            z: (cursorZ + dz) * SCALE_IN_TO_UNIT,
          },
          rotationY: cursorRot,
          pitchAngle,
          lengthIn: sec.lengthIn,
          widthIn: config.widthIn,
        });
        cIn += sec.lengthIn;
      }
    }

    // ── Rails for this segment (left + right, mid-height) ──
    if (config.railType !== 'aucun') {
      const xOff = config.widthIn / 2; // inches — perpendicular to travel
      const dxMid = (Math.sin(cursorRot) * seg.runIn) / 2;
      const dzMid = (Math.cos(cursorRot) * seg.runIn) / 2;
      // Perpendicular unit vector (left = +perp, right = -perp when rot=0 → perpX=cos, perpZ=-sin)
      const perpCos = Math.cos(cursorRot);
      const perpSin = Math.sin(cursorRot);

      for (const side of ['left', 'right'] as const) {
        const sideSign = side === 'left' ? -1 : 1;
        const perpXIn = perpCos * xOff * sideSign;
        const perpZIn = -perpSin * xOff * sideSign;
        rails.push({
          id: `rail-sb${idx}-${side}`,
          position: {
            x: (cursorX + dxMid + perpXIn) * SCALE_IN_TO_UNIT,
            y: segMidYIn * SCALE_IN_TO_UNIT,
            z: (cursorZ + dzMid + perpZIn) * SCALE_IN_TO_UNIT,
          },
          rotationY: cursorRot,
          pitchAngle,
          lengthIn: seg.runIn,
          heightIn: rh,
          type: config.railType as 'ajoure' | 'barrotin' | 'barrotin_mains',
          side,
        });
      }
    }

    // ── Pattes for this segment ──
    const patteCount = Math.max(1, Math.floor(seg.runIn / PATTE_INTERVAL_IN));
    for (let i = 1; i <= patteCount; i++) {
      const fraction = i / (patteCount + 1);
      const patteRunIn = seg.runIn * fraction;
      const patteYIn = heightRemaining - fraction * segHeight;
      if (patteYIn < 1) continue;
      const patteDx = Math.sin(cursorRot) * patteRunIn;
      const patteDz = Math.cos(cursorRot) * patteRunIn;
      pattes.push({
        id: `patte-sb${idx}-${i}`,
        position: {
          x: (cursorX + patteDx) * SCALE_IN_TO_UNIT,
          y: patteYIn * SCALE_IN_TO_UNIT,
          z: (cursorZ + patteDz) * SCALE_IN_TO_UNIT,
        },
        rotationY: cursorRot,
        widthIn: config.widthIn,
        heightIn: patteYIn,
        partNumber: selectPatteSku(patteYIn),
      });
    }

    // ── Advance cursor to end of this segment ──
    cursorX += Math.sin(cursorRot) * seg.runIn;
    cursorZ += Math.cos(cursorRot) * seg.runIn;
    heightRemaining -= segHeight;

    if (idx < shape.segments.length - 1) {
      // ── Corner posts at junction (top of direction change, left + right) ──
      if (config.railType !== 'aucun') {
        const cpSku = selectCornerPostSku(rh);
        const cpXOff = config.widthIn / 2;
        const perpCos = Math.cos(cursorRot);
        const perpSin = Math.sin(cursorRot);
        for (const side of ['left', 'right'] as const) {
          const sideSign = side === 'left' ? -1 : 1;
          const cpPerpXIn = perpCos * cpXOff * sideSign;
          const cpPerpZIn = -perpSin * cpXOff * sideSign;
          cornerPosts.push({
            id: `cp-junction-${idx}-${side}`,
            position: {
              x: (cursorX + cpPerpXIn) * SCALE_IN_TO_UNIT,
              y: heightRemaining * SCALE_IN_TO_UNIT,
              z: (cursorZ + cpPerpZIn) * SCALE_IN_TO_UNIT,
            },
            heightIn: rh,
            partNumber: cpSku,
          });
        }
      }

      // ── Turn landing ──
      const next = shape.segments[idx + 1]!;
      const turn = next.direction === 'left' ? 1 : next.direction === 'right' ? -1 : 0;
      const halfLanding = TURN_LANDING_SIZE_IN / 2;
      const lx = cursorX + Math.sin(cursorRot) * halfLanding;
      const lz = cursorZ + Math.cos(cursorRot) * halfLanding;
      platforms.push({
        id: `turn-${idx}`,
        kind: 'turn-landing',
        position: {
          x: lx * SCALE_IN_TO_UNIT,
          y: heightRemaining * SCALE_IN_TO_UNIT,
          z: lz * SCALE_IN_TO_UNIT,
        },
        widthIn: TURN_LANDING_SIZE_IN,
        depthIn: TURN_LANDING_SIZE_IN,
      });
      cursorX += Math.sin(cursorRot) * TURN_LANDING_SIZE_IN;
      cursorZ += Math.cos(cursorRot) * TURN_LANDING_SIZE_IN;
      cursorRot += turn * (Math.PI / 2);
    }
  });

  // ── Corner posts at ground end of last segment (left + right) ──
  if (config.railType !== 'aucun') {
    const cpSku = selectCornerPostSku(rh);
    const cpXOff = config.widthIn / 2;
    const perpCos = Math.cos(cursorRot);
    const perpSin = Math.sin(cursorRot);
    for (const side of ['left', 'right'] as const) {
      const sideSign = side === 'left' ? -1 : 1;
      const cpPerpXIn = perpCos * cpXOff * sideSign;
      const cpPerpZIn = -perpSin * cpXOff * sideSign;
      cornerPosts.push({
        id: `cp-ground-${side}`,
        position: {
          x: (cursorX + cpPerpXIn) * SCALE_IN_TO_UNIT,
          y: 0,
          z: (cursorZ + cpPerpZIn) * SCALE_IN_TO_UNIT,
        },
        heightIn: rh,
        partNumber: cpSku,
      });
    }
  }

  // ── Transition plate at ground end of last segment ──
  // Offset 5" past the last segment end in travel direction
  const transDx = Math.sin(cursorRot) * 5;
  const transDz = Math.cos(cursorRot) * 5;
  transitions.push({
    id: 'trans-base',
    position: {
      x: (cursorX + transDx) * SCALE_IN_TO_UNIT,
      y: 0,
      z: (cursorZ + transDz) * SCALE_IN_TO_UNIT,
    },
    rotationY: cursorRot,
    widthIn: config.widthIn,
    partNumber: selectTransitionSku(config.widthIn, config.floor),
  });

  const hardwareGroups: HardwareGroup[] = [];

  return {
    platforms,
    treads,
    rails,
    landings,
    pattes,
    cornerPosts,
    transitions,
    hardwareGroups,
    floor: config.floor,
  };
}
